import { computed, ref, toValue, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import {
  useGgFollowup,
  useGgFollowupSelection,
} from 'dashboard/composables/useGgFollowup';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import GgSupportHeaderAPI from 'dashboard/api/ggSupportHeader';

export const GG_SUPPORT_ATTRIBUTE_KEY = 'suporte';

export function useGgSupportTickets(
  conversationId,
  { loadOnMount = true } = {}
) {
  const { enabled } = useGgFollowup('support', conversationId);
  const { selection } = useGgFollowupSelection();
  const currentChat = useMapGetter('getSelectedChat');
  const request = useAbortableRequest();
  const tickets = ref([]);
  const loaded = ref(false);
  const error = ref(false);
  const linkingAvailable = ref(false);
  const statusActionsAvailable = ref(false);
  const id = computed(() => toValue(conversationId));
  const supportNotes = computed(() =>
    currentChat.value.id === id.value
      ? (currentChat.value.messages || [])
          .filter(
            message =>
              message.additional_attributes?.gg_support_operation_id ||
              message.additional_attributes?.gg_support_state_history_id ||
              message.additional_attributes?.gg_support_followup ||
              message.additional_attributes?.gg_support_link_ids
          )
          .map(message => message.id)
          .join(',')
      : ''
  );

  const load = async () => {
    if (!enabled.value) return;
    error.value = false;
    loaded.value = false;
    try {
      const response = await request.run(signal =>
        GgSupportHeaderAPI.get(id.value, { signal })
      );
      if (!response) return;
      tickets.value = response.data.tickets;
      linkingAvailable.value = response.data.linking_available;
      statusActionsAvailable.value = response.data.status_actions_available;
      loaded.value = true;
    } catch {
      tickets.value = [];
      error.value = true;
    }
  };
  const refresh = () => {
    if (loadOnMount || loaded.value) load();
  };

  watch(
    [id, enabled],
    () => {
      request.abort();
      tickets.value = [];
      loaded.value = false;
      error.value = false;
      linkingAvailable.value = false;
      statusActionsAvailable.value = false;
      if (loadOnMount) load();
    },
    { immediate: true }
  );
  // Notes arrive only after saving. Closing either entry point also refreshes
  // saved receipts when publication of the private note is still pending.
  watch(supportNotes, refresh);
  watch(
    () => selection.conversationId,
    (activeId, previousId) => {
      if (previousId === id.value && activeId !== id.value) refresh();
    }
  );

  return {
    enabled,
    tickets,
    loaded,
    error,
    linkingAvailable,
    statusActionsAvailable,
    busy: request.isPending,
    load,
  };
}
